import React from "react";
import { createPortal } from "react-dom";
import styled, { createGlobalStyle } from "styled-components";
import {
  Modal,
  colors2,
  UI_BODY_SEMIBOLD,
  UI_BODY_SM,
  ButtonPrimaryV2,
  ButtonGhostV2,
} from "oolib";
import { globalScrollbarStyling } from "oolib/dist/v2/themes/globalStyles";
import { FieldTypeList } from "../FieldTypeList";

/**
 * "Getting Started" overlay (Figma: "Verify Mapping") shown on top of the
 * DirectDataImport preview/table view every time a fresh preview loads. It
 * explains the field-type mapping and lists every imported field alongside the
 * field type the server auto-detected for it. The types are READ-ONLY here —
 * actual editing still happens in the column headers of the table underneath.
 *
 * - I Understand: dismiss and reveal the table (also the X and backdrop click).
 */

/**
 * Our own backdrop, same split as DirectDataImportModal + ImportModalShell: the
 * Modal is passed hideBackdrop (keeping its click-outside-to-close but dropping
 * its tint) and we render the design's tint here instead — oolib's own backdrop
 * is a fixed black_opacity80 with no blur, which this design isn't.
 *
 * Modal portals out to <body> at z-index 999999999, above the whole
 * DirectDataImportModal subtree this sits in, so it always layers over this.
 */
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: #0013320d;
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  z-index: 999999998;
`;

/**
 * oolib's Modal exposes no style hook for its card, but it deliberately sets
 * className="StyledModalLargeContentWrapper" on it, and the card portals to
 * #modal-root. Reach it from here to apply the design's drop shadow. Only
 * mounted while this modal is (no other oolib Modal renders alongside it in this
 * flow), so it's effectively scoped to it.
 */
const ModalShadowStyle = createGlobalStyle`
  #modal-root .StyledModalLargeContentWrapper {
    box-shadow: 0px 4px 20px 0px #00000026;
  }
`;

const HEADER_HEIGHT = 44; // per the design's header frame

/**
 * Modal is passed overflowVisible so it never scrolls itself; the field list is
 * the only scroll container. That means our content has to stay inside Modal's
 * own 90dvh surface cap, so the list's ceiling is that cap less this modal's
 * fixed chrome: the 44px header, the ~65px footer (14px padding either side of
 * a 36px button) and the body's 14px padding top and bottom. Rounded up, so the
 * surface always fits rather than spilling past Modal's rounded edge.
 */
const FIELD_LIST_MAX_HEIGHT = `calc(90dvh - 150px)`;

const Header = styled.div`
  height: ${HEADER_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 10px 10px 14px;
  border-bottom: 1px solid ${colors2.grey10};
`;

const Body = styled.div`
  display: flex;
  gap: 14px;
  padding: 14px;
`;

const LeftText = styled.div`
  flex: 0 0 255px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const VDivider = styled.div`
  width: 1px;
  align-self: stretch;
  background: ${colors2.grey10};
`;

// The one scroll container in the modal.
const FieldListScrollArea = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  max-height: ${FIELD_LIST_MAX_HEIGHT};
  overflow-y: auto;
  ${globalScrollbarStyling}
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  padding: 14px;
  border-top: 1px solid ${colors2.grey10};
`;

const CloseButton = styled(ButtonGhostV2)`
  padding: 4px;
  margin-right: -4px;
`;

export const GettingStartedModal = ({ outline = [], onContinue }) => {
  return (
    <>
      <ModalShadowStyle />
      {createPortal(<Backdrop />, document.getElementById("modal-root"))}
      <Modal
        title="Getting Started"
        onClose={onContinue}
        disableHeader
        fitToContentHeight
        overflowVisible
        hideBackdrop
        showActionPanel={false}
        desktopWidth="74.8rem"
      >
        <Header>
          <UI_BODY_SEMIBOLD color={colors2.grey90}>
            Getting Started
          </UI_BODY_SEMIBOLD>
          <CloseButton icon="X" onClick={onContinue} />
        </Header>

        <Body>
          <LeftText>
            <UI_BODY_SM color={colors2.grey90}>
              All your data is imported into a dataset, where each column becomes
              a field and each row becomes a document.
            </UI_BODY_SM>
            <UI_BODY_SM color={colors2.grey90}>
              Fields support different data types, enabling different ways to
              organize, analyze, and visualize your data. Choosing the correct
              field type helps you get the most out of your dataset.
            </UI_BODY_SM>
            <UI_BODY_SM color={colors2.grey90}>
              We've automatically assigned field types based on your data. Since
              automatic detection isn't always perfect, we recommend reviewing
              them before continuing.
            </UI_BODY_SM>
          </LeftText>

          <VDivider />

          {/* Types are read-only here — editing happens in the table's column headers. */}
          <FieldListScrollArea>
            <FieldTypeList outline={outline} readOnly />
          </FieldListScrollArea>
        </Body>

        <Footer>
          <ButtonPrimaryV2 onClick={onContinue}>I Understand</ButtonPrimaryV2>
        </Footer>
      </Modal>
    </>
  );
};

export default GettingStartedModal;
